import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { BrandLogo } from "@/components/Logo";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, Loader2, ShieldCheck } from "lucide-react";

const empty = { business_name: "", contact_person: "", mobile: "", whatsapp: "", email: "", address: "", category: "MSME", loan_category: "", loan_amount: "", mela_interest: "" };

export default function PublicLead() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [err, setErr] = useState("");
  const [f, setF] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    api.get(`/public/form/${token}`).then((r) => setMeta(r.data)).catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || "Invalid link"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.business_name || !f.mobile) return setErr("Please enter your name and mobile number");
    if (!f.mela_interest) return setErr("Please tell us if you're interested to attend our Loan Mela");
    setLoading(true); setErr("");
    try {
      await api.post(`/public/form/${token}`, { ...f, loan_amount: Number(f.loan_amount) || 0 });
      setDone(true);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A192F] flex items-center justify-center p-4" style={{ background: "radial-gradient(700px circle at 20% 10%, #0F4C81, #0A192F), radial-gradient(600px circle at 90% 90%, #0B6E4F55, transparent)" }}>
      <div className="w-full max-w-xl">
        <div className="flex justify-center mb-6"><BrandLogo height={70} /></div>
        {err && !meta ? (
          <Card className="p-8 text-center" data-testid="public-error"><p className="text-rose-600 font-semibold">{err}</p><p className="text-sm text-slate-500 mt-2">Please contact your relationship manager for a valid link.</p></Card>
        ) : done ? (
          <Card className="p-10 text-center" data-testid="public-success">
            <CheckCircle2 className="mx-auto text-emerald-500 mb-4" size={56} />
            <h2 className="text-2xl font-extrabold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Thank you! 🎉</h2>
            <p className="text-slate-600 mt-2">Your registration has been received{meta?.rm_name ? ` by ${meta.rm_name}` : ""}. Our team will contact you shortly with your Loan Mela invitation details.</p>
          </Card>
        ) : (
          <Card className="p-6 sm:p-8">
            <h1 className="text-2xl font-extrabold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Loan Mela Invitation</h1>
            <p className="text-sm text-slate-500 mt-1 mb-5">{meta?.rm_name ? `Invited by ${meta.rm_name}. ` : ""}Register below and our team will contact you with your Loan Mela details.</p>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label className="mb-1.5 block">Business / Full Name *</Label><Input data-testid="public-business-name" value={f.business_name} onChange={(e) => set("business_name", e.target.value)} /></div>
                <div><Label className="mb-1.5 block">Contact Person</Label><Input value={f.contact_person} onChange={(e) => set("contact_person", e.target.value)} /></div>
                <div><Label className="mb-1.5 block">Mobile *</Label><Input data-testid="public-mobile" value={f.mobile} onChange={(e) => set("mobile", e.target.value)} /></div>
                <div><Label className="mb-1.5 block">WhatsApp</Label><Input value={f.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} /></div>
                <div><Label className="mb-1.5 block">Email</Label><Input value={f.email} onChange={(e) => set("email", e.target.value)} /></div>
                <div><Label className="mb-1.5 block">City / Address</Label><Input value={f.address} onChange={(e) => set("address", e.target.value)} /></div>
                <div>
                  <Label className="mb-1.5 block">Category</Label>
                  <Select value={f.category} onValueChange={(v) => set("category", v)}>
                    <SelectTrigger data-testid="public-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{(meta?.categories || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="mb-1.5 block">Loan Category</Label>
                  <Select value={f.loan_category} onValueChange={(v) => set("loan_category", v)}>
                    <SelectTrigger data-testid="public-loan-category"><SelectValue placeholder="Select loan type" /></SelectTrigger>
                    <SelectContent>{(meta?.loan_categories || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2"><Label className="mb-1.5 block">Loan Amount Required (₹)</Label><Input type="number" data-testid="public-loan-amount" value={f.loan_amount} onChange={(e) => set("loan_amount", e.target.value)} /></div>
                <div className="sm:col-span-2">
                  <Label className="mb-1.5 block">Are you interested to attend our Loan Mela? *</Label>
                  <Select value={f.mela_interest} onValueChange={(v) => set("mela_interest", v)}>
                    <SelectTrigger data-testid="public-mela-interest"><SelectValue placeholder="Select Yes / No" /></SelectTrigger>
                    <SelectContent><SelectItem value="Yes">Yes</SelectItem><SelectItem value="No">No</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              {err && <div className="text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">{err}</div>}
              <Button type="submit" disabled={loading} data-testid="public-submit" className="w-full h-11 bg-[#0F4C81] hover:bg-[#0A3A66] text-base font-semibold">
                {loading ? <Loader2 className="animate-spin" size={18} /> : "Submit Registration"}
              </Button>
              <p className="text-xs text-slate-400 flex items-center justify-center gap-1.5"><ShieldCheck size={13} className="text-emerald-500" />Your information is secure and shared only with your loan advisor.</p>
            </form>
          </Card>
        )}
      </div>
    </div>
  );
}
